import React, { useCallback, useEffect, useState } from "react";
import { MessageSquareReply, Search } from 'lucide-react';
import { debounce, getPractice, numberWithCommas } from "../../../helpers";
import { useDocumentTitle } from '../../../hooks/customDocumentTitleHook';
import { fetchFidelisIncomingMessage } from '../../../services/fidelisPatient';
import DataTablePagination from '../../DataTablePagination';
import Spacing from '../../Spacing';
import IncomingFidelisMessageDataTable from '../../DataTable/IncomingFidelisMessageDataTable';
import Filter from "../../Filter";

export default function IncomingFidelisMessages({selectedPracticeChange, fetchChangePracticeData}) {
  let location_id = getPractice("location_id");
  let account_id = getPractice("account_id");
  if(selectedPracticeChange != null && fetchChangePracticeData){
    location_id = selectedPracticeChange?.location_id;
    account_id = selectedPracticeChange?.account_id;
  }

  const [dataLoading, setDataLoading] = useState(true);
  const [data, setData] = useState([]);
  const [filter, setFilter] = useState({
    page: 1,
    per_page: 10,
    account_id: account_id,
    location_id: location_id,
    search_term: "",
    start_date: "",
    end_date: "",
    order_by: [],
  });
  const [patientFormData, setPatientFormData] = useState(filter);

  const [dataCount, setDataCount] = useState({
    totalCount: 0,
    totalPages: 0
  });

  useEffect(() => {
    loadData(filter);
  }, [filter]);

  const loadData = async (getFilter) => {
    setFilter(getFilter);
    let isCanceled = false;

    try {
      const response = await fetchFidelisIncomingMessage(getFilter);
      if (response) {
        setData(response?.data?.results);
        setDataCount({
          totalCount: response?.data?.count,
          totalPages: response?.data?.total_pages,
        });
      }
    } catch (error) {
      if (error?.code === "ERR_CANCELED" || error?.name === "CanceledError") {
        isCanceled = true;
      }
    } finally {
      if (!isCanceled) {
        setDataLoading(false);
      }
    }
  };

  const handlePageChange = (newPage) => {
    setFilter({ ...filter, page: newPage });
    setDataLoading(true);
  };

  const debouncedSetFilter = useCallback(
    debounce((newFilter) => setFilter(newFilter), 300), []
  );

  const handleChangeSearchTerm = (event) => {
    const newFilter = { ...filter, search_term: event.target.value, page: 1 };
    setDataLoading(true);
    debouncedSetFilter(newFilter);
  };

  const handleChangeRowsPerPage = (value) => {
    setFilter({
      ...filter,
      page: 1,
      per_page: value,
    });
    setDataLoading(true);
  };

  useEffect(() => {
    const sameLocation = filter.location_id === location_id;
    const sameAccount = filter.account_id === account_id;

    if (sameLocation && sameAccount) return;

    setFilter({
      ...filter,
      location_id: location_id,
      account_id: account_id,
    });
    setDataLoading(true);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [location_id, account_id]);


  useDocumentTitle("Incoming Message");

  return (
    <>
      <div className="docpro_page_heading_wrap">
        <h2 className="docpro_page_heading docpro_fs_24">
          <i
            className="docpro_white_color"
            style={{ backgroundColor: "#898E87" }}
          >
            <MessageSquareReply width={20} height={20} />
          </i>
          Incoming Message
          <span>{numberWithCommas(dataCount.totalCount)}</span>
        </h2>
      </div>
      <Spacing lg="12" sm="12" />
      <div className="docpro_data_table_wrap">
        <div className="docpro_data_table_headings small_heading docpro_grid_gap_10">
          <div className="docpro_data_table_heading_input_group">
            <form className="docpro_search_form">
              <input
                type="text"
                placeholder="Search"
                className="docpro_search_input"
                onChange={handleChangeSearchTerm}
              />
              <button className="docpro_search_btn">
                <Search width={20} height={20} />
              </button>
            </form>
          </div>

          <div className="docpro_data_table_btns small_filter_btns p-0">
            <Filter
              filterType="fidelis_incoming"
              reloadData={setFilter}
              reloadDataFilter={filter}
              setDataLoading={setDataLoading}
              patientFormData={patientFormData}
              setPatientFormData={setPatientFormData}
            />
          </div>
        </div>
        <IncomingFidelisMessageDataTable
          tableData={data}
          reloadData={loadData}
          reloadDataFilter={filter}
          dataLoading={dataLoading}
          setDataLoading={setDataLoading}
        />
        {(data.length > 0 && !dataLoading) && (
          <DataTablePagination
            currentPage={filter.page}
            totalPages={dataCount.totalPages}
            onPageChange={handlePageChange}
            handleChangeRowsPerPage={handleChangeRowsPerPage}
            totalRecords={dataCount.totalCount}
            currentPageData={filter.per_page}
          />
        )}
      </div>
    </>
  );
}